import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Send, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InlineChatPopup = ({ isOpen, onClose, clientId, providerId, serviceId, serviceName, clientName, token }) => {
  const [inquiry, setInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (isOpen && clientId && providerId) {
      fetchConversation();
      // Poll for new messages every 5 seconds
      pollRef.current = setInterval(fetchConversation, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId, providerId, serviceId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversation = async () => {
    try {
      const params = { client_id: clientId, provider_id: providerId };
      if (serviceId) params.service_id = serviceId;
      const res = await axios.get(`${API}/inquiries/find`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.inquiry) {
        setInquiry(res.data.inquiry);
        setMessages(res.data.messages || []);
      } else {
        setInquiry(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !inquiry) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/inquiries/${inquiry.id}/message`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      await fetchConversation();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  const isDisabled = inquiry?.disabled;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="inline-chat-popup">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '75vh' }}>
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{clientName || 'Client'}</h3>
              <p className="text-xs text-gray-500">{serviceName || 'Booking'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-200 rounded-full transition" data-testid="close-chat-popup">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50" style={{ minHeight: '250px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
          ) : !inquiry ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm">
              <MessageCircle className="w-10 h-10 mb-2 opacity-40" />
              <p>No conversation yet.</p>
              <p className="text-xs mt-1">Start a conversation from the Inquiries page.</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">No messages yet</div>
          ) : (
            <>
              {messages.map((msg, i) => {
                const isMine = msg.sender_id === providerId;
                return (
                  <div key={msg.id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${
                      isMine ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                    }`}>
                      {msg.message_type === 'image' && msg.image_url && (
                        <img src={msg.image_url} alt="Shared" className="max-w-full rounded-lg mb-1" />
                      )}
                      {msg.message && <p>{msg.message}</p>}
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-blue-200' : 'text-gray-400'}`}>
                        {formatDate(msg.created_at)} {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        {inquiry && !isDisabled ? (
          <form onSubmit={handleSend} className="px-4 py-3 border-t bg-white rounded-b-2xl flex gap-2 shrink-0">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-3 py-2 text-sm border rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              disabled={sending}
              data-testid="inline-chat-input"
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="bg-blue-600 text-white p-2.5 rounded-full hover:bg-blue-700 disabled:opacity-40 transition"
              data-testid="inline-chat-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        ) : isDisabled ? (
          <div className="px-4 py-3 border-t bg-gray-100 text-center text-sm text-gray-500 rounded-b-2xl">
            This conversation has been disabled.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default InlineChatPopup;
